export type OtpChallengeTone = "error" | "neutral" | "pending" | "success"

export interface RegistrationOtpChallengeState {
  debugOtp: string
  description: string
  expiresAt: number
  phoneNumber: string
  resendAvailableAt: number
  sendLoading: boolean
  timerActive: boolean
  title: string
  tone: OtpChallengeTone
  verificationId: string | null
  verified: boolean
  verifyLoading: boolean
}

export interface OtpChallengeTimers {
  expiresRemainingMs: number
  resendRemainingMs: number
}

export interface StartedOtpChallenge {
  debugOtp?: string | null
  expiresInSec?: number | null
  phoneNumber: string
  resendAfterSec?: number | null
  verificationId: string
}

export function createRegistrationOtpChallengeState(): RegistrationOtpChallengeState {
  return {
    debugOtp: "",
    description:
      "Enter the main phone number above, then request a six-digit verification code.",
    expiresAt: 0,
    phoneNumber: "",
    resendAvailableAt: 0,
    sendLoading: false,
    timerActive: false,
    title: "Verification required",
    tone: "neutral",
    verificationId: null,
    verified: false,
    verifyLoading: false,
  }
}

export function startOtpChallenge(
  challenge: RegistrationOtpChallengeState,
  payload: StartedOtpChallenge,
  now = Date.now()
): RegistrationOtpChallengeState {
  return {
    ...challenge,
    debugOtp: payload.debugOtp?.trim() ?? "",
    description: `A six-digit code was sent to ${maskPhoneNumber(payload.phoneNumber)}. Enter it below to continue.`,
    expiresAt: now + (Math.max(payload.expiresInSec ?? 0, 0) * 1000),
    phoneNumber: payload.phoneNumber,
    resendAvailableAt: now + (Math.max(payload.resendAfterSec ?? 0, 0) * 1000),
    sendLoading: false,
    timerActive: true,
    title: "Code sent",
    tone: "pending",
    verificationId: payload.verificationId,
    verified: false,
    verifyLoading: false,
  }
}

export function markOtpSending(
  challenge: RegistrationOtpChallengeState
): RegistrationOtpChallengeState {
  return {
    ...challenge,
    sendLoading: true,
    tone: challenge.verified ? "success" : challenge.tone,
  }
}

export function markOtpVerifying(
  challenge: RegistrationOtpChallengeState
): RegistrationOtpChallengeState {
  return {
    ...challenge,
    verifyLoading: true,
  }
}

export function markOtpVerified(
  challenge: RegistrationOtpChallengeState
): RegistrationOtpChallengeState {
  return {
    ...challenge,
    description: `${maskPhoneNumber(challenge.phoneNumber)} has been verified and can now be saved with this registration.`,
    timerActive: false,
    title: "Phone verified",
    tone: "success",
    verified: true,
    verifyLoading: false,
  }
}

export function failOtpChallenge(
  challenge: RegistrationOtpChallengeState,
  message: string
): RegistrationOtpChallengeState {
  return {
    ...challenge,
    description: message,
    sendLoading: false,
    title: challenge.verificationId ? "Verification failed" : "Unable to send code",
    tone: "error",
    verifyLoading: false,
    verified: false,
  }
}

export function resetOtpChallenge(
  message?: string
): RegistrationOtpChallengeState {
  return {
    ...createRegistrationOtpChallengeState(),
    description:
      message
      ?? "Enter the main phone number above, then request a six-digit verification code.",
  }
}

export function getOtpChallengeTimers(
  challenge: RegistrationOtpChallengeState,
  now = Date.now()
): OtpChallengeTimers {
  return {
    expiresRemainingMs: Math.max(0, challenge.expiresAt - now),
    resendRemainingMs: Math.max(0, challenge.resendAvailableAt - now),
  }
}

export function maskPhoneNumber(value: string): string {
  const trimmed = value.trim()

  if (trimmed.length <= 4) {
    return trimmed
  }

  return `${trimmed.slice(0, 3)}${"*".repeat(Math.max(0, trimmed.length - 7))}${trimmed.slice(-4)}`
}

export function formatOtpCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
}
